import {
  HotkeyCallback,
  RegisterableHotkey,
  UseHotkeyOptions,
  useHotkey,
} from '@tanstack/react-hotkeys';
import { isEmpty, isNil } from 'lodash';
import { useCallback } from 'react';
import { useBezel } from '@shared/components/Bezel';
import { useGlobalHotkey } from './useGlobalHotkey';
import { useHotkeysEnabled } from './useHotkeysEnabled';

export interface HotKeyProps {
  hotkey: RegisterableHotkey | null | undefined;
  callback: HotkeyCallback;
  enabled?: boolean;
  /** Set global to suppress this hotkey when modals/menus are open */
  global?: boolean;
  /** Shows a bezel message when the hotkey fires */
  bezelMessage?: string;
  options?: Omit<UseHotkeyOptions, 'enabled'>;
}

const defaultOptions: UseHotkeyOptions = {
  conflictBehavior: 'allow',
};

/**
 * Register a hotkey for the lifetime of the parent component, subject to the
 * hotkeys feature flag.
 */
export function HotKey({
  hotkey,
  callback,
  enabled = true,
  global,
  bezelMessage,
  options,
}: HotKeyProps) {
  const hotkeysEnabled = useHotkeysEnabled();
  if (!enabled || !hotkeysEnabled || isNil(hotkey)) {
    return null;
  }

  if (global) {
    return (
      <RegisterGlobalHotKey
        hotkey={hotkey}
        callback={callback}
        options={options}
        bezelMessage={bezelMessage}
      />
    );
  } else {
    return (
      <RegisterHotKey
        hotkey={hotkey}
        callback={callback}
        options={options}
        bezelMessage={bezelMessage}
      />
    );
  }
}

interface RegisterHotKeyProps {
  hotkey: RegisterableHotkey;
  callback: HotkeyCallback;
  options?: UseHotkeyOptions;
  bezelMessage?: string;
}

function RegisterHotKey({ hotkey, callback, options, bezelMessage }: RegisterHotKeyProps) {
  const bezelCallback = useBezelCallback(callback, bezelMessage);
  const hotkeyOptions = { ...defaultOptions, ...options };
  useHotkey(hotkey, bezelCallback, hotkeyOptions);
  return null;
}

function RegisterGlobalHotKey({ hotkey, callback, options, bezelMessage }: RegisterHotKeyProps) {
  const bezelCallback = useBezelCallback(callback, bezelMessage);
  const hotkeyOptions = { ...defaultOptions, ...options };
  useGlobalHotkey(hotkey, bezelCallback, hotkeyOptions);
  return null;
}

/**
 * Wraps `callback` so invoking the hotkey also fires a bezel.
 */
function useBezelCallback(callback: HotkeyCallback, bezelMessage?: string): HotkeyCallback {
  const bezel = useBezel();
  return useCallback(
    (...args: Parameters<HotkeyCallback>) => {
      callback(...args);
      if (!isNil(bezelMessage) && !isEmpty(bezelMessage)) {
        requestAnimationFrame(() => bezel({ message: bezelMessage }));
      }
    },
    [bezel, bezelMessage, callback],
  );
}
